'use client';

import { PREFERENCES_STORAGE_KEY } from "@/lib/constants/preferences-client";
import { UserSettingsItem } from "@/lib/types/user/settings";

// The list of UI-specific fields stored in metaData lives in
// UI_PREFERENCE_FIELDS in @/lib/constants/preferences-client.

// Check if localStorage is available
function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

// Fallback memory storage for environments where localStorage isn't available
const memoryStore: Record<string, string> = {};

/**
 * Save user preferences to localStorage for non-logged-in users
 */
export function saveLocalPreferences(field: string, value: any): void {
  try {
    // Get existing preferences or initialize new object
    const existingPrefs = loadLocalPreferences();

    // Update the specific field
    const updatedPrefs = {
      ...existingPrefs,
      [field]: value,
      updatedAt: new Date().toISOString()
    };

    // Save back to storage
    if (isLocalStorageAvailable()) {
      localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(updatedPrefs));
    } else {
      memoryStore[PREFERENCES_STORAGE_KEY] = JSON.stringify(updatedPrefs);
    }
  } catch (error) {
    console.error('Failed to save preferences to localStorage:', error);
  }
}

/**
 * Load user preferences from localStorage for non-logged-in users
 */
export function loadLocalPreferences(): Partial<UserSettingsItem> {
  try {
    let prefsString: string | null = null;

    if (isLocalStorageAvailable()) {
      prefsString = localStorage.getItem(PREFERENCES_STORAGE_KEY);
    } else {
      prefsString = memoryStore[PREFERENCES_STORAGE_KEY] || null;
    }

    if (!prefsString) return {};

    return JSON.parse(prefsString);
  } catch (error) {
    console.error('Failed to load preferences from storage:', error);
    return {};
  }
}

/**
 * Clear user preferences from localStorage
 */
export function clearLocalPreferences(): void {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.removeItem(PREFERENCES_STORAGE_KEY);
    } else {
      delete memoryStore[PREFERENCES_STORAGE_KEY];
    }
  } catch (error) {
    console.error('Failed to clear preferences from storage:', error);
  }
}

/**
 * Initialize Redux store with preferences from localStorage
 */
export function initializePreferencesFromStorage(): Partial<UserSettingsItem> {
  // This function should be called when the app initializes
  // to load preferences from localStorage into Redux
  return loadLocalPreferences();
}

/**
 * Check if there are local preferences that need to be synced with the server
 * This is useful when a user has authenticated and we need to determine if
 * there are any local preferences that should be synced
 */
export function hasLocalPreferencesToSync(): boolean {
  const prefs = loadLocalPreferences();
  return Object.keys(prefs).length > 0;
}

// Re-export extractUIPreferences from shared utils for backward compatibility
export { extractUIPreferences } from "@/lib/utils/preferences";
